import { FC, useEffect, useState } from "react";
import CustomSelect from "../../../components/forms/CustomSelect";
import RequiredInput from "../../../components/forms/InputHOC";
import { toast } from "sonner";
import { useFormik } from "formik";
import useHandleErrors from "../../../hooks/useHandleErrors";
import useCompanies from "../../../hooks/api-calls/useCompanies";
import { usePrivilege } from "../../../components/priviledge/PriviledgeProvider";
import * as yup from "yup";
import { useAtom } from "jotai";
import { formsSubmitAtom } from "../../../atom/atom";
import { Textarea } from "rizzui";
import { UnitMeasureService } from "../../../services/unitMeasure/unitMeasureService";
import { UnitMeasure } from "../../../type/entities/unit-measure-type";

interface UnitMeasureFormProps {    
    setOpenModal: any;
    refetch: () => void;
    unitMeasure?: UnitMeasure | null;
    formRef?: any;
}

const schema = yup.object({
    name: yup.string().required('El nombre es obligatorio'),
    symbol: yup.string().required('El símbolo es obligatorio'),
    description: yup.string().optional(),
})

const superAdminSchema = schema.concat(yup.object({
    company: yup.string().required('La compañía es obligatoria'),
}))

const UnitMeasureForm:FC<UnitMeasureFormProps> = ({ setOpenModal, refetch, unitMeasure, formRef }) => {

    // STATES

    const service = new UnitMeasureService();
    const isEdit = unitMeasure !== null;
    const [isSearched, setIsSearched] = useState<boolean>(false);
    
    // HOOKS
    
    const [, setIsSubmitting]       = useAtom(formsSubmitAtom);
    const { handleErrors }          = useHandleErrors();
    const { getCompaniesList }      = useCompanies();
    const { userCan }               = usePrivilege();

    // FORMIK

    const formik = useFormik({
        initialValues: {
            id: unitMeasure?.id ?? '',
            name: unitMeasure?.name ?? '',
            symbol: unitMeasure?.symbol ?? '',
            description: unitMeasure?.description ?? '',
            company: unitMeasure?.company?.id ?? ''
        },
        validationSchema: userCan('list', 'company') ? superAdminSchema : schema,
        validateOnBlur: false,
        onSubmit: values => { 
            if(!isEdit) {
                handleCreate(values)
            } else {
                handleEdit(values)
            }
        },
    });


    // FUNCTIONS

    //-----------------------------------------------------------------------------------------------------------------
    /**
     * @ES CREAR UNA NUEVA UNIDAD DE MEDIDA
     * @EN CREATE A NEW UNIT MEASURE
     * 
     * @param values
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleCreate = async (values: any) => {
        try {
            const response = await (await service.createUnit(values)).getResponseData();
            if (response.success) {
                refetch();
                setOpenModal(false);
                formik.resetForm();
                toast.success('Unidad de medida creada correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //-----------------------------------------------------------------------------------------------------------------
    

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES EDITAR UNA UNIDAD DE MEDIDA
     * @EN EDIT A UNIT MEASURE   
     * 
     * @param values
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleEdit = async (values: any) => {
        try {
            const response = await (await service.editUnit(values)).getResponseData();   
            if (response.success) {
                refetch();
                setOpenModal(false);
                formik.resetForm();
                toast.success('Unidad de medida editada correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //-------------------------------------------------------------------------------------------------------------

    // USE EFFECT

    useEffect(() => {
        setIsSubmitting(formik.isSubmitting);
    }, [formik.isSubmitting, setIsSubmitting]);

    useEffect(() => {
        if (!formik.values.company && getCompaniesList().length > 0 && !isSearched) {
            setIsSearched(true);
            let defaultCompany = getCompaniesList()?.find((g: any) => g.label == 'GESTION DE RESIDUOS 2023 SLU');
            formik.setFieldValue('company', defaultCompany?.value ?? '');
        }
    }, [getCompaniesList]);

    // RENDER

    return (
         <div className="@container">
            <form onSubmit={formik.handleSubmit} ref={formRef ? formRef : undefined}>
                <div className={'md:col-span-12'}>
                    <div className='md:grid md:grid-cols-12 gap-4'>

                        <RequiredInput
                            id='name'
                            label="Nombre"
                            className='md:col-span-12'
                            formik={formik}
                            error={formik.errors.name}  
                        />

                        <RequiredInput
                            id='symbol'
                            label="Símbolo"
                            className='md:col-span-12'
                            formik={formik}
                            error={formik.errors.symbol}  
                        />

                        <Textarea
                            id='description'
                            label='Descripción'
                            rows={4}
                            value={formik.values.description}
                            onChange={(e) => formik.setFieldValue('description', e.target.value)}
                            className="mb-4 md:col-span-12"
                        />

                        {userCan('list', 'company') && (
                            <CustomSelect
                            required
                            isSearchable
                            id={'company'}
                            label="Compañia"
                            containerClassName='md:col-span-12'
                            value={
                                formik.values.company ?
                                { value: formik.values.company, label: getCompaniesList()?.find((g: any) => g.value === formik.values.company)?.label } :
                                getCompaniesList()?.find((g: any) => g.label == 'GESTION DE RESIDUOS 2023 SLU')
                            }
                            options={getCompaniesList()}
                            onChange={
                                (e: any) => { formik.setFieldValue('company', e?.value) }
                            }
                            error={formik.errors.company}
                            />
                        )}                    
                    </div>
                </div>
            </form>
        </div>
    );
}

export default UnitMeasureForm;
